import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star, ThumbsUp, Share2, Flag } from "lucide-react";

// 10 hand-crafted sovereign-client testimonials. All 5-star, varied service
// mix (vaulting, pouch, air courier, customs), varied geographies and dates.
const REVIEWS = [
  {
    name: "Amelia Hartwell",
    role: "Foreign & Commonwealth Office · UK",
    date: "Dec 4, 2025",
    initials: "AH",
    color: "bg-emerald-300",
    avatar: "/reviewers/r1.webp",
    body: "We routed six classified dispatches through their sovereign air courier over the last quarter — every single handover was signed off inside the SLA, and the audit trail we received back was the cleanest my team has ever seen. Genuinely peace-of-mind service.",
    replied: true,
  },
  {
    name: "Amb. K. Nyström",
    role: "Swedish Embassy · Vienna",
    date: "Nov 18, 2025",
    initials: "KN",
    color: "bg-amber-300",
    avatar: "/reviewers/r2.webp",
    body: "The pouch handling protocol they operate is what every diplomatic mission wishes their carrier could deliver. Two-officer custody, tamper-evident wax seals, and a coordinator who answered on the first ring at 03:00 CET. Exceptional.",
    replied: true,
  },
  {
    name: "Rohan Mehta",
    role: "Ministry of External Affairs · New Delhi",
    date: "Oct 22, 2025",
    initials: "RM",
    color: "bg-sky-300",
    avatar: "/reviewers/r3.webp",
    body: "Cross-border consignment, four transit hubs, zero incidents. The public tracking timeline was accurate to the minute — recipient at the other end could actually follow the corridor rather than guessing. Will absolutely use them again.",
    replied: true,
  },
  {
    name: "Sophie Laurent",
    role: "Consulat général de France · London",
    date: "Sep 30, 2025",
    initials: "SL",
    color: "bg-rose-300",
    avatar: "/reviewers/r4.webp",
    body: "We entrusted them with a particularly sensitive artefact for our archives. The Class III vault storage plus their dedicated custodian meant we could sleep at night. Cost is not the lowest but the discretion is worth every pound.",
    replied: false,
  },
  {
    name: "Marcus Chen",
    role: "Sovereign Wealth Fund · Singapore",
    date: "Aug 15, 2025",
    initials: "MC",
    color: "bg-violet-300",
    avatar: "/reviewers/r5.webp",
    body: "Moved a portfolio of bearer instruments from Zurich to Singapore in under 48 hours with a chain of custody that satisfied our risk committee on the first pass. Their custody certificate is now something we specifically request from any custodian.",
    replied: true,
  },
  {
    name: "Col. J. Rothwell",
    role: "MOD Procurement · UK",
    date: "Jul 8, 2025",
    initials: "JR",
    color: "bg-emerald-300",
    avatar: "/reviewers/r6.webp",
    body: "Armored ground service on a high-risk lane. Route obfuscation was excellent, communications on the encrypted channel were disciplined, and the officers behaved with a professionalism you'd expect from a service that started at FCDO. Recommended without reservation.",
    replied: true,
  },
  {
    name: "Isabela Costa",
    role: "Embaixada do Brasil · Berlin",
    date: "Jun 12, 2025",
    initials: "IC",
    color: "bg-yellow-300",
    avatar: "/reviewers/r7.webp",
    body: "Wonderful team. What impressed us most was the response time on a sudden re-routing — a corridor closure meant we needed a new plan inside two hours, and they had a compliant alternative and revised paperwork ready before we finished asking for it.",
    replied: true,
  },
  {
    name: "Hiroshi Tanaka",
    role: "Consulate of Japan · Zurich",
    date: "Apr 27, 2025",
    initials: "HT",
    color: "bg-fuchsia-300",
    avatar: "/reviewers/r8.webp",
    body: "Discreet. Punctual. Meticulous. The three qualities we most needed in a partner, and Diplomatic Vaults delivered all three across four separate consignments this year. Their coordinators genuinely understand the sensitivities of consular work.",
    replied: false,
  },
  {
    name: "Aisha Al-Sabah",
    role: "Kuwait Foreign Ministry",
    date: "Mar 3, 2025",
    initials: "AS",
    color: "bg-teal-300",
    avatar: "/reviewers/r9.webp",
    body: "Customs clearance across three jurisdictions in one week. Every single paper was in order and every stop was handled without a phone call from our end. Their end-to-end logistics service saved my team probably forty hours of coordination.",
    replied: true,
  },
  {
    name: "Sir Reginald Ashcombe",
    role: "Private custody client",
    date: "Feb 14, 2025",
    initials: "RA",
    color: "bg-orange-300",
    avatar: "/reviewers/r10.webp",
    body: "I am not easy to please and I do not write reviews. I have made an exception here because the standard of custody, the calibre of their staff, and the confidentiality with which they operate is genuinely without peer in this country. I remain very grateful.",
    replied: true,
  },
];

function TrustStars() {
  return (
    <div className="flex gap-[3px]" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={`star-${i}`}
          className="w-5 h-5 rounded-sm bg-emerald-500 flex items-center justify-center"
        >
          <Star className="w-3 h-3 fill-white text-white" strokeWidth={0} />
        </span>
      ))}
    </div>
  );
}

function ReviewerAvatar({ r }) {
  const [failed, setFailed] = useState(false);
  if (r.avatar && !failed) {
    return (
      <img
        src={r.avatar}
        alt={`Profile photo of ${r.name}`}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="w-12 h-12 rounded-full object-cover shrink-0 border-2 border-amber-500/40 shadow-md shadow-black/40"
      />
    );
  }
  return (
    <div
      className={`w-12 h-12 rounded-full ${r.color} text-slate-950 flex items-center justify-center font-serif text-lg font-bold shrink-0 border-2 border-amber-500/40`}
      aria-label={`Initials of ${r.name}`}
    >
      {r.initials}
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <article
      className="flex-shrink-0 w-full md:w-[calc(50%-12px)] snap-start bg-[#0F1C3F]/80 backdrop-blur-md border border-amber-500/20 rounded-2xl p-6 md:p-7 flex flex-col"
      data-testid="review-card"
    >
      <header className="flex items-center gap-4">
        <ReviewerAvatar r={r} />
        <div className="min-w-0">
          <div className="font-serif text-lg text-slate-50 truncate">{r.name}</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-amber-400/70 truncate">
            {r.role}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{r.date}</div>
        </div>
      </header>
      <div className="mt-5">
        <TrustStars />
      </div>
      <p className="mt-4 text-slate-200 leading-relaxed flex-1">{r.body}</p>
      {r.replied && (
        <div className="mt-5 flex items-center gap-2 text-xs text-slate-400 font-mono uppercase tracking-widest">
          <Flag className="w-3 h-3 text-amber-400/70" /> Company replied
        </div>
      )}
      <div className="mt-5 pt-5 border-t border-amber-500/10 flex items-center gap-6 text-xs text-slate-400">
        <button className="flex items-center gap-1.5 hover:text-amber-300 transition-colors">
          <ThumbsUp className="w-3.5 h-3.5" /> Useful
        </button>
        <button className="flex items-center gap-1.5 hover:text-amber-300 transition-colors">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button className="flex items-center gap-1.5 hover:text-amber-300 transition-colors ml-auto">
          <Flag className="w-3.5 h-3.5" />
        </button>
      </div>
    </article>
  );
}

export default function ReviewsCarousel() {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [activeDot, setActiveDot] = useState(0);

  // Deps intentionally empty: only reads refs and calls stable setters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    // Which card is nearest the left edge? Divide by card width.
    const cardWidth = el.firstChild?.offsetWidth || 1;
    setActiveDot(Math.round(el.scrollLeft / (cardWidth + 24)));
  }, []);

  useEffect(() => {
    updateState();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener("resize", updateState);
    return () => {
      el.removeEventListener("scroll", updateState);
      window.removeEventListener("resize", updateState);
    };
  }, [updateState]);

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.offsetWidth || 320;
    el.scrollBy({ left: dir * (cardWidth + 24), behavior: "smooth" });
  };

  return (
    <section
      className="border-y border-amber-500/15 bg-[#0A1128]"
      data-testid="reviews-carousel"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div className="max-w-xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400/80 mb-3">
              What sovereign clients say
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight">
              A record of trusted custody.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 mr-3">
              <TrustStars />
              <span className="ml-2 text-sm text-slate-300 font-mono">
                4.9 · {REVIEWS.length} reviews
              </span>
            </div>
            <button
              onClick={() => scrollByCard(-1)}
              disabled={!canPrev}
              aria-label="Previous reviews"
              data-testid="reviews-prev"
              className="w-11 h-11 rounded-full border border-amber-500/40 text-amber-300 flex items-center justify-center hover:bg-amber-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollByCard(1)}
              disabled={!canNext}
              aria-label="Next reviews"
              data-testid="reviews-next"
              className="w-11 h-11 rounded-full border border-amber-500/40 text-amber-300 flex items-center justify-center hover:bg-amber-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 lg:-mx-10 px-6 lg:px-10"
          style={{ scrollbarWidth: "none" }}
        >
          <style>{`[data-testid='reviews-carousel'] .flex::-webkit-scrollbar{display:none}`}</style>
          {REVIEWS.map((r, i) => (
            <ReviewCard key={`${r.name}-${i}`} r={r} />
          ))}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          {REVIEWS.map((r, i) => (
            <span
              key={`dot-${r.name}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === activeDot ? "w-6 bg-amber-400" : "w-1.5 bg-amber-500/30"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
